import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, FileText, PlayCircle, Link as LinkIcon, StickyNote, ChevronDown, ChevronUp, Sparkles } from "lucide-react";

const LANG_TABS = [
  { code: "en", label: "🇺🇸 Inglés", color: "#3B82F6" },
  { code: "fr", label: "🇫🇷 Francés", color: "#8B5CF6" },
  { code: "es", label: "🇪🇸 Español", color: "#DC2626" },
];

const TYPE_ICON = { pdf: FileText, video: PlayCircle, link: LinkIcon, note: StickyNote };

export default function AdminLanguages() {
  const [activeLang, setActiveLang] = useState("en");
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async (lang) => {
    setLoading(true);
    try {
      const r = await api.get("/languages/admin/materials", { params: { lang } });
      setMaterials(r.data.materials || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudieron cargar los materiales");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(activeLang);
  }, [activeLang]);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este material? También se borra su examen IA cacheado.")) return;
    try {
      await api.delete(`/languages/admin/materials/${id}`);
      toast.success("Material eliminado");
      load(activeLang);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div data-testid="admin-languages-page" className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] inline-flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5" /> Idiomas · Material adicional
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A] mb-2">
          Sube material para tu equipo.
        </h1>
        <p className="text-[#6B7280] max-w-2xl">
          Comparte PDFs, videos, enlaces o notas por idioma. Los vendedores los verán como "Material adicional"
          en el mapa del idioma. Al terminar un video, la IA puede generar un examen de 15 preguntas basado
          en el resumen.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#E5E7EB]">
        {LANG_TABS.map((t) => (
          <button
            key={t.code}
            onClick={() => setActiveLang(t.code)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
              activeLang === t.code
                ? "border-[#1B2E7A] text-[#1B2E7A]"
                : "border-transparent text-[#6B7280] hover:text-[#1A1A1A]"
            }`}
            data-testid={`tab-${t.code}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <MaterialForm lang={activeLang} onAdded={() => load(activeLang)} />

      <section>
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-4">
          Materiales en {LANG_TABS.find((t) => t.code === activeLang).label} · {materials.length}
        </p>
        {loading ? (
          <div className="text-[#374151]">Cargando...</div>
        ) : materials.length === 0 ? (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-10 text-center">
            <p className="text-[#6B7280]">Aún no hay materiales para este idioma. Sube el primero arriba.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {materials.map((m) => (
              <MaterialRow key={m.id} material={m} onDelete={remove} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function MaterialRow({ material, onDelete }) {
  const Icon = TYPE_ICON[material.resource_type] || FileText;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5 flex items-start gap-3" data-testid={`mat-row-${material.id}`}>
      <div className="h-10 w-10 rounded-lg bg-[#F3F4F6] text-[#1B2E7A] flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">{material.resource_type}</p>
        <h3 className="font-heading text-lg text-[#1A1A1A] leading-tight truncate">{material.title}</h3>
        {material.description && <p className="text-xs text-[#6B7280] mt-1 line-clamp-2">{material.description}</p>}
        {material.transcript && (
          <p className="text-[10px] text-[#1B2E7A] mt-1 inline-flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Tiene transcripción · examen IA listo
          </p>
        )}
      </div>
      <button
        onClick={() => onDelete(material.id)}
        className="text-[#DC2626] hover:bg-[#DC2626]/10 p-1.5 rounded-md"
        data-testid={`del-mat-${material.id}`}
        aria-label="Eliminar"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

function MaterialForm({ lang, onAdded }) {
  const fileRef = useRef();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    resource_type: "video",
    url_type: "url",
    url: "",
    body: "",
    transcript: "",
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiTopic, setAiTopic] = useState("");

  const reset = () => {
    setForm({
      title: "",
      description: "",
      resource_type: "video",
      url_type: "url",
      url: "",
      body: "",
      transcript: "",
    });
    setAiTopic("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const generateAI = async () => {
    if (!form.title) {
      toast.error("Pon un título primero para que la IA tenga contexto.");
      return;
    }
    setAiBusy(true);
    try {
      const r = await api.post("/languages/admin/materials/ai/generate", {
        title: form.title,
        lang,
        topic: aiTopic,
        resource_type: form.resource_type,
      });
      setForm((f) => ({
        ...f,
        description: r.data.description || f.description,
        body: r.data.body || f.body,
        transcript: r.data.transcript || f.transcript,
      }));
      toast.success("Material generado con IA · revisa y guarda");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando con IA");
    } finally {
      setAiBusy(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((p) => ({ ...p, url: data.path, url_type: "upload" }));
      toast.success("Archivo subido");
    } catch {
      toast.error("Error al subir");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.title) return toast.error("Título requerido");
    if (form.resource_type !== "note" && !form.url) return toast.error("Sube archivo o agrega URL");
    if (form.resource_type === "note" && !form.body) return toast.error("La nota necesita contenido");
    setSubmitting(true);
    try {
      await api.post("/languages/admin/materials", { lang, ...form });
      toast.success("Material agregado");
      reset();
      setOpen(false);
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const acceptByType = form.resource_type === "pdf" ? "application/pdf" : "video/*";

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl" data-testid="material-form">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-4 hover:bg-[#FFFFFF] rounded-2xl"
        data-testid="toggle-mat-form"
      >
        <div className="flex items-center gap-2">
          <Plus className="h-4 w-4 text-[#1B2E7A]" />
          <span className="font-medium text-sm text-[#1A1A1A]">Subir material en {lang.toUpperCase()}</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && (
        <div className="border-t border-[#E5E7EB] p-5 space-y-3">
          <div className="grid sm:grid-cols-2 gap-2">
            <select
              value={form.resource_type}
              onChange={(e) => setForm({ ...form, resource_type: e.target.value, url: "", url_type: e.target.value === "link" ? "url" : "upload" })}
              className="border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white"
              data-testid="mat-type"
            >
              <option value="video">Video</option>
              <option value="pdf">PDF</option>
              <option value="link">Enlace</option>
              <option value="note">Nota / texto</option>
            </select>
            <Input
              placeholder="Título"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="mat-title"
            />
          </div>

          <Input
            placeholder="Descripción breve (opcional, ayuda a generar el examen IA)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="mat-description"
          />

          {/* AI generation block */}
          <div className="bg-[#FEF3C7] border border-[#F4B81F]/40 rounded-xl p-3" data-testid="mat-ai-block">
            <p className="text-xs text-[#854D0E] mb-2 inline-flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" /> Crear material con ayuda de IA · Claude Sonnet 4.5
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                placeholder="Tema o foco (ej. recibir cliente extranjero)"
                value={aiTopic}
                onChange={(e) => setAiTopic(e.target.value)}
                className="flex-1"
                data-testid="mat-ai-topic"
              />
              <Button
                onClick={generateAI}
                disabled={aiBusy}
                className="bg-[#F4B81F] hover:bg-[#D49538] text-[#1A1A1A]"
                data-testid="mat-ai-gen"
              >
                {aiBusy ? "Generando..." : "Generar con IA"}
              </Button>
            </div>
            <p className="text-[10px] text-[#6B7280] mt-2">
              La IA llena descripción, contenido y transcripción usando el título y el tema.
            </p>
          </div>

          {form.resource_type === "note" ? (
            <Textarea
              placeholder="Contenido de la nota..."
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={5}
              data-testid="mat-body"
            />
          ) : form.resource_type === "link" ? (
            <Input
              placeholder="URL https://..."
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              data-testid="mat-url"
            />
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <select
                  value={form.url_type}
                  onChange={(e) => setForm({ ...form, url_type: e.target.value, url: "" })}
                  className="border border-[#E5E7EB] rounded-md px-3 py-1.5 text-xs bg-white"
                >
                  <option value="upload">Subir archivo</option>
                  <option value="url">URL externa</option>
                </select>
                {form.url_type === "upload" ? (
                  <>
                    <input
                      ref={fileRef}
                      type="file"
                      accept={acceptByType}
                      onChange={handleUpload}
                      className="text-sm"
                      data-testid="mat-file"
                    />
                    {uploading && <span className="text-xs text-[#737373]">Subiendo...</span>}
                    {form.url && !uploading && <span className="text-xs text-[#1B2E7A]">Listo</span>}
                  </>
                ) : (
                  <Input
                    placeholder="URL https://... (YouTube, Vimeo, etc.)"
                    value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                    className="flex-1"
                    data-testid="mat-url"
                  />
                )}
              </div>
            </div>
          )}

          {form.resource_type === "video" && (
            <div className="bg-[#FEF3C7] border border-[#F4B81F]/40 rounded-xl p-3">
              <p className="text-xs text-[#854D0E] mb-2 inline-flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" /> Resumen / transcripción del video (recomendado)
              </p>
              <Textarea
                placeholder="Pega aquí el resumen o transcripción del video. La IA generará un examen de 15 preguntas basado en este texto. Si lo dejas vacío, las preguntas serán generales del título y descripción."
                value={form.transcript}
                onChange={(e) => setForm({ ...form, transcript: e.target.value })}
                rows={5}
                data-testid="mat-transcript"
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => { reset(); setOpen(false); }}>Cancelar</Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-[#1B2E7A] hover:bg-[#13205A] text-white"
              data-testid="submit-mat"
            >
              <Plus className="h-4 w-4 mr-1" /> {submitting ? "Guardando..." : "Agregar"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
